import { calculateBitrate, getImageInfo, getVideoPixel } from '@/utils/dw'
import { formatSizeUnits } from '@/utils/dw/common'
import Monjs from '@ks-kdocs-kmon/monjs'
import merge from 'lodash/merge'
import Vue from 'vue'
import DwManager from '@/utils/dw/DwManager'

export type KmonConfig = {
  base: {
    dsn: string; //（必填，字符串）由Kmon平台注册项目时自动生成，为url
    ver: string; //（必填，字符串）当前业务版本号
    env: string; //（必填，字符串）当前业务环境,
    userid?: string; //（选填，字符串）当前用户的userid
    fileid?: string; // (选填，字符串）文件ID，传递能表述文件ID的值即可
    corpid?: string; // (选填，字符串）企业ID，没有则传空字符串即可
    referer?: string; //  (选填，字符串）引用
  };
  feature: {
    isEnableReport: boolean; // (必填，布尔值）启用监控SDK，默认false
    isEnableError?: boolean; // (选填，布尔值）开启页面错误监控，默认false
    isEnablePerformance?: boolean; // (选填，布尔值）开启页面性能监控，默认false
    isEnableLongTask?: boolean; // (选填，布尔值）开启长耗时监控，默认false
  };
};

export type VideoInfo = {
  width: number;
  height: number;
  duration: number;
  poster: string;
}

export type AudioInfo = {
  duration: number;
}

export type ReportShowOptions<T = VideoInfo> = {
  fileInfo: {
    fsize: number;
    fname: string;
  };
  mediaInfo: T;
};

export type ReportType = 'show' | 'play' | 'media_api' | 'user_active'
export type ReportPayload = (ReportShowOptions | ReportPlayOptions | Record<string, any>) & { fileType: 'video' | 'audio' }

export type ReportPlayOptions = { fileInfo: ReportShowOptions['fileInfo'] | null } & Record<string, any>

type RecursivePartial<T> = {
  [K in keyof T]?: T[K] extends object ? RecursivePartial<T[K]> : T[K]
};

let monjs

const defaultConfig: KmonConfig = {
  base: {
    dsn: process.env.VUE_APP_KMON_DSN,
    ver: process.env.VUE_APP_VERSION,
    env: process.env.VUE_APP_KMON_ENV
  },
  feature: {
    isEnableReport: true,
    isEnableError: true,
    isEnablePerformance: true,
    isEnableLongTask: true
  }
}

function initKmon(config?: RecursivePartial<KmonConfig>) {
  const mergedConfig: KmonConfig = merge(defaultConfig, config)
  const { base, feature } = mergedConfig
  monjs = new Monjs(base, feature)
}

function setKmonConfig(config: Omit<Partial<KmonConfig['base']>, 'dsn'>) {
  return monjs.setConfig(config)
}

async function report(
  type: ReportType,
  payload: ReportPayload
) {
  const dispatch = {
    async show() {
      const { fileType } = payload
      let value: any
      if (fileType === 'video') {
        const { fileInfo, mediaInfo } = payload as ReportShowOptions<VideoInfo>
        const imageInfo = await getImageInfo(mediaInfo.poster)
  
        value = {
          size: formatSizeUnits(fileInfo.fsize),
          type: fileInfo.fname.split('.').pop()?.toUpperCase(),
          pixel: getVideoPixel(mediaInfo.width, mediaInfo.height),
          code_rate: calculateBitrate(fileInfo.fsize, mediaInfo.duration),
          duration: mediaInfo.duration.toFixed(2),
          is_cover: !!mediaInfo.poster,
          ...imageInfo
        }
      } else {
        const { fileInfo, mediaInfo } = payload as ReportShowOptions<AudioInfo>
        const imageInfo = await getImageInfo('')
        value = {
          size: formatSizeUnits(fileInfo.fsize),
          type: fileInfo.fname.split('.').pop()?.toUpperCase(),
          duration: mediaInfo.duration.toFixed(2),
          code_rate: calculateBitrate(fileInfo.fsize, mediaInfo.duration),
          pixel: '',
          is_cover: false,
          ...imageInfo
        }
      }
      monjs.kmon({
        type,
        value
      })
    },

    play() {
      let { fileInfo, ...rest } = payload as ReportPlayOptions

      !fileInfo && (fileInfo = {
        fname: '',
        fsize: 0
      })

      monjs.kmon({
        type,
        value: {
          size: formatSizeUnits(fileInfo.fsize),
          type: fileInfo.fname.split('.').pop()?.toUpperCase(),
          ...rest
        }
      })
    },

    media_api() {
      monjs.kmon({
        type,
        value: payload
      })
    },

    user_active() {
      monjs.kmon({
        type,
        value: payload
      })
    }
  }

  dispatch[type]()
}

const kmon = {
  initKmon,
  setKmonConfig,
  report
}

export function useKmon() {
  kmon.initKmon()
  Vue.prototype.$kmon = kmon
  Vue.prototype.$DwManager = new DwManager(kmon.report)
}
